import * as React from 'react'
import { useState, useEffect, useRef } from 'react'
import { GridSeeker, SubmitButtonSeeker, AccordionCardSeeker, ButtonSeeker } from "ui-seeker/core"
import { mergeDicts } from 'ui-seeker/utils'

import { useTranslation } from 'react-i18next'
import { translateBoard, translateRate, translateDevice } from 'app/@utils/catalog'
import { useCommonFilters, useFilterSection } from './hooks'
import {
    SelectAsyncHotels,
    SelectAlertType,
    SelectAlertSubtype,
    SelectComparison,
    SelectIkey,
    SelectAlerts, SelectMarket
} from 'app/components'
import {
	CustomDateRangePicker,
    CustomDatePicker,
	SelectDateRange, 
    DateSpecificDays,
    SelectBoard, 
	SelectHotels, 
    SelectHotelsHistorical,
    SelectYear, 
	SelectRate,
	SelectRooms, 
    SelectRawRooms,
	SelectNights,
    SelectDevice,
    SelectDeviceStatic,
    SelectPax,
    SelectPaxStatic,
    SelectSources,
    SelectSite,
    SelectOtas,
    SelectConnectivities,
    SelectConnectivitiesStatic,
    SelectSiteLocation,
    SelectMappingType,
    RadioDevicesMultiButton,
    RadioAlertMultiButton,
    SwitchMinStay,
    SelectOccupationAdults,
    SelectOccupationKids,
    SelectNightsStatic,
    SelectCityFrom,
    SelectCityTo,
    SelectLocation,
    SelectInfo,
    SelectProvince,
    SelectCalendarType,
    SelectCompetitionSites
} from "./components"
import CommonFiltersActions from './actions'
import CommonFiltersAlertActions from './AlertActions'
import {FEATURES} from "../../@common/constants";


export default function CommonFilters({ onSubmit, onCancel, components={}, compProps={}, isValid = true, buttonLabel = null, page = FEATURES.COMPETITION }) {

    const { t } = useTranslation('common')
    const section = useFilterSection()
    const [transitioning, setTransitioning] = useState(false)

    useEffect(() => {
        if (isValid) {
            setTransitioning(true)
            const timeout = setTimeout(() => setTransitioning(false), 300)
            return () => clearTimeout(timeout)
        }
    }, [isValid])

    // Ref used by the otas onAjax to detect whether the AJAX refresh was
    // triggered by a route change (reset selection to all) or by some other
    // dep like date-range (preserve selection).
    const prevOtasRouteRef = useRef(undefined)

    const {
		hotels, dateRange, customDateRange, maxDate, boardID, rateID, roomID, devices, otas, connectivities, ikey,
        occupancy, sites, sitesList, sources, nights, priceType, isLoading, useCustomDate, comparison, customDate,
        deviceParity, mainHotels, siteLocation, allHotels, mappingType, alerts, alertName, alertType, alertSubtype, 
        minStay, adults, kids, nightsStatic, siteName, cityFrom, cityTo, monthYear, market,
        rawRooms, location, hotelHistorical, year, otherSites, elementsOtherSites, devicesStatic, lastDate, info,
        province, specificDays, calendarType, totalSites, realtimeMarket,

		setHotels, setDateRange, setCustomDateRange, setMaxDate, setBoard, setRate, setRooms, setDevices, setLocation,
        setOtas, setOccupancy, setSites, setSitesList, setNights, setPriceType, setDeviceParity, setCustomDate,
        setSources, setConnectivities, setSiteLocation, setAllHotels, setMappingType, setTotalSites,
        setAlerts, setAlertName, setAlertType, setAlertSubtype, setComparison, setIkeys, setRawRooms,
        setDefaultOccupancy, setMinStay, setAdults, setKids, setNightsStatic, setCityFrom,
        setCityTo, setHotelHistorical, setYear, setOtherSites, setDevicesStatic, setDefaultNights, setInfo, setProvince,
        setSpecificDays, setCalendarType, setDefaultOtas, setRealtimeMarket,

        handleLoadingChange
    } = useCommonFilters()


    const myComps = {
        // Dates Filters
        dateRange: false, customDateRange: true, customDate: false, year: false, customSpecificDays: false,
        specificDays: false,
        // Items Filters
        hotels: true, board: true, rate: true, rooms: true, occupancy: true, occupancyStatic: false, sites: true, 
        sitesList: false, nights: true, priceType: true, devices: true, payType: true, devicesParity: false, 
        sources: false, otas: false, connectivities: false, connectivitiesStatic: false, realtimeMarket: false, siteLocation: false, allHotels: false,
        mappingType: false, minStay: false, adults: false, kids: false, nightsStatic: false, cities: false,
        rawRooms: false, location: false, hotelHistorical: false, otherSites: false, devicesStatic: false, info: false,
        province: false,
        // Alerts filters
        alerts: false, alertName: false, alertType: false, alertSubtype: false, comparison: false,
        // Filter Components
        filterBtn: true, buttonsActions: true, buttonsAlertActions: false,
        ...components
    }

    const myCompProps = mergeDicts({
        dateRange: {
            grid: { size: 2 },
            value: dateRange, onChange: setDateRange, onAjax: setMaxDate, mainHotels: mainHotels,
            onLoadingChange: (isLoading) => handleLoadingChange("dateRange", isLoading)
        }, customDateRange: {
            grid: { size: 2 },
            value: customDateRange, onChange: setCustomDateRange, max: maxDate, placement: 'bottomEnd', lastDate: lastDate
        }, customDate: {
            grid: { size: 2 },
            value: customDate, onChange: setCustomDate, placement: 'bottomEnd'
        }, specificDays: {
            grid: { size: 2 },
            value: specificDays, onChange: setSpecificDays, placement: 'bottomEnd'
        }, calendarType: {
            grid: { size: 2 },
            value: calendarType, onChange: setCalendarType
        }, hotels: {
            grid: { size: 2 }, method: 'POST', selectedRoute: connectivities, customDates: customDateRange, date: monthYear,
            value: hotels, mainHotels: mainHotels, placeholder: t('filters.allCompetitors'), isClearable: false, section: section,
            onChange: setHotels,
            onAjax: ((options) => {
                const MAX_HOTELS_SELECTED = 25
                const validHotels = hotels.filter(h => options.some(opt => opt.value === h))
                if (!validHotels?.length || myCompProps?.hotels?.id_report_execution ) setHotels(options?.slice(0, Math.min(options.length, MAX_HOTELS_SELECTED)))
                else if (validHotels?.length !== hotels?.length) setHotels(options.filter(opt => validHotels.includes(opt.value)))

                return options
            }),
            onLoadingChange: (isLoading) => handleLoadingChange("hotels", isLoading)
        }, hotelHistorical: {
            grid: { size: 3, className: !location ? 'hotel-historical-disabled' : '' },
            value: hotelHistorical, onChange: setHotelHistorical, location: location,
            onLoadingChange: (isLoading) => handleLoadingChange("hotelHistorical", isLoading)
        }, year: {
            grid: { size: 2 },
            value: year, onChange: setYear
        }, otas: {
            grid: { size: 2 },
            value: otas, onChange: setOtas, placeholder: t('filters.allSites'), selectedRoute: connectivities, mainHotels: mainHotels,
            customDates: customDateRange, date: monthYear, section: section,
            onAjax: (options = []) => {
                setDefaultOtas(options)
                if (!options.length) return options
                // Two distinct refresh triggers reach this onAjax:
                //   - date-range change (same `selectedRoute`): user wants to
                //     KEEP their previous site selection across months.
                //   - route change: the previous selection is no longer
                //     meaningful (different destination → potentially
                //     different sites). Reset to "All sites" of the new route.
                // We distinguish by comparing `connectivities` against the
                // previous value captured in a ref.
                const routeChanged = prevOtasRouteRef.current !== connectivities
                prevOtasRouteRef.current = connectivities
                if (routeChanged) {
                    setOtas(options)
                    return options
                }
                const current = [].concat(otas).filter(Boolean)
                if (!current.length) {
                    setOtas(options)
                    return options
                }
                const valid = current.filter(id => options.some(opt => opt.value === id))
                if (!valid.length) setOtas(options)
                else if (valid.length !== current.length) setOtas(valid.map(id => ({ value: id })))
                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("otas", isLoading)
        }, connectivities: {
            grid: { size: 2 },
            value: connectivities, onChange: setConnectivities, mainHotels: mainHotels, date: monthYear, customDates: customDateRange, section: section,
            onAjax: (options = []) => {
                const valueExists = options.some(opt => opt.value === connectivities)
                if (!valueExists && options.length > 0) {
                    setConnectivities(options[0])
                }
                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("connectivities", isLoading)
        }, board: {
            grid: { size: 2 }, method: 'POST',
            // i18n: traduce SOLO el texto mostrado (meal plan) por id_board; value/label intactos, se envía el id.
            formatOptionLabel: opt => translateBoard(opt?.value, opt?.label),
            value: boardID, onChange: setBoard, mainHotels: mainHotels, rate: rateID || priceType, room: rawRooms, date: monthYear,
            nights: nights, occupancy: occupancy, devices: devices, hotels: hotels, sites: sites || sitesList,
            customDates: customDateRange, info: info, sitePackageID: otas, selectedRoute: connectivities, section: section,
            onAjax: (options = []) => {
                if (!options.length) return options
                const boards = [].concat(boardID).filter(Boolean)
                const validBoards = boards.filter(id => options.some(opt => opt.value === id))
                if (validBoards.length !== boards.length)
                    setBoard(validBoards.length ? validBoards.map(id => ({value: id})) : {value: null})
                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("board", isLoading)
        }, rate: {
            grid: { size: 2 },
            // i18n: traduce SOLO el texto mostrado (cancellation policy) por id_rate; value/label intactos, se envía el id.
            formatOptionLabel: opt => translateRate(opt?.value, opt?.label),
            value: rateID, onChange: setRate, mainHotels: mainHotels, placeholder: t('filters.allCancellationPolicy'), board: boardID,
            room: rawRooms, date: monthYear, nights: nights, occupancy: occupancy, devices: devices, hotels: hotels,
            sites: sites || sitesList, customDates: customDateRange, info: info, sitePackageID: otas, selectedRoute: connectivities, section: section,
            onAjax: (options = []) => {
                const valueExists = options.some(opt => opt.value === rateID)
                if(!valueExists) setRate({value: null})
                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("rate", isLoading)
        }, rooms: {
            grid: { size: 2 },
            value: roomID, onChange: setRooms, mainHotels: mainHotels
        }, rawRooms: {
            grid: { size: 4 }, method: 'POST',
            value: rawRooms, onChange: setRawRooms, board: boardID, rate: rateID || priceType, date: monthYear,
            nights: nights, occupancy: occupancy, devices: devices, hotels: hotels, sites: sites || sitesList,
            customDates: customDateRange, info: info, sitePackageID: otas, selectedRoute: connectivities, section: section,
            onAjax: (options = []) => {
                const validRawRooms = rawRooms.filter(room => options.some(opt => opt.value === room))
                if(rawRooms?.length !== validRawRooms?.length) setRawRooms(validRawRooms)
                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("rawRooms", isLoading)
        }, occupancy: {
            grid: { size: 2 }, method: 'POST',
            value: occupancy, onChange: setOccupancy, mainHotels: mainHotels, sites: sites || sitesList, devices: devices,
            nights: nights, date: monthYear, customDates: customDateRange, selectedRoute: connectivities, sitePackageID: otas, section: section,
            onAjax: (options = []) => {
                const valueExists = options.some(opt => opt.value === occupancy)
                if (!valueExists && options?.length) {
                    const defaultOccupancy = options.find(opt => opt.value === '20') || options[0]
                    setOccupancy(defaultOccupancy)
                    setDefaultOccupancy(defaultOccupancy)
                }

                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("occupancy", isLoading)
        }, sources: {
            grid: { size: 2 },
            value: sources, onChange: setSources,
            onLoadingChange: (isLoading) => handleLoadingChange("sources", isLoading)
        }, sitesList: {
            grid: { size: 2 },
            value: sitesList, onChange: setSitesList, mainHotels: mainHotels, source: sources, date: monthYear,
            customDates: customDateRange, alertType: alertType, market: market, section: section,
            onLoadingChange: (isLoading) => handleLoadingChange("sitesList", isLoading)
        }, allHotels: {
            grid: { size: 3 },
            value: allHotels, onChange: setAllHotels
        }, siteLocation: {
            grid: { size: 2 },
            value: siteLocation, onChange: setSiteLocation
        }, location: {
            grid: { size: 2 },
            value: location, onChange: setLocation,
            onLoadingChange: (isLoading) => handleLoadingChange("location", isLoading)
        }, province: {
            grid: { size: 2 },
            value: province, onChange: setProvince
        }, sites: {
            grid: { size: 4 },
            value: sites, onChange: setSites, market: market, mainHotels: mainHotels, date: monthYear,
            customDates: customDateRange, totalSites: totalSites,
            items: [
                { label: 'Booking', value: 192, button: 1 },
                { label: 'Expedia', value: 311, button: 2 },
                { label: t('filters.officialSite'), value: 'official_site', button: 3 },
            ],
            onAjax: (options = []) => {
                const valueExists = options.some(opt => opt.value === sites)
                if (!valueExists) options?.length ? setSites(options[0]) : setSites({value: null})
                setTotalSites(options.length)
                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("sites", isLoading)
        }, otherSites: {
            grid: { size: 2, className: !elementsOtherSites.length && 'select-other-sites-disabled', style: { '.selectSeeker-wrapper': { borderRadius: '0 6px 6px 0' } } },
            value: otherSites, onChange: setOtherSites, isDisabled: !elementsOtherSites.length, isLoading: false, date: monthYear
        }, devicesParity: {
            grid: { size: 3 },
            value: deviceParity, onChange: setDeviceParity, market: market, mainHotels: mainHotels, date: monthYear,
            customDates: customDateRange, sites: sitesList, nights: nights, occupancy: occupancy, 
            items: [
                { label: t('filters.desktop'), value: 1 },
                { label: t('filters.mobile'), value: 2 },
            ]
        }, alerts: {
            grid: { size: 4 },
            value: alerts, onChange: setAlerts,
            items: [
                { label: t('filters.alertCompetition'), value: 'competition' },
                { label: t('filters.alertParity'), value: 'parity' },
                { label: t('filters.alertPackages'), value: 'packages' },
            ]
        }, alertName: {
            grid: { size: 2 },
            value: alertName, onChange: setAlertName, sites: sitesList, date: monthYear,
            onLoadingChange: (isLoading) => handleLoadingChange("alertName", isLoading)
        }, alertType: {
            grid: { size: alerts === 'parity' && alertType !== '9' ? 2 : 4 },
            label: "", placeholder: t('filters.allAlertTypes'), value: alertType,
            onChange: e => setAlertType(e), isClearable: false, isMulti: false, tabAlertType: alerts,
            onLoadingChange: (isLoading) => handleLoadingChange("alertType", isLoading)
        }, alertSubtype: {
            grid: { size: 2 },
            label: "", placeholder: t('filters.allAlertsSubtypes'), value: alertSubtype,
            onChange: e => setAlertSubtype(e), isClearable: false, isMulti: false, alertType: alertType
        }, comparison: {
            grid: { size: 2 },
            label: "", placeholder: t('filters.allComparisons'), value: comparison, isLoading: false,
            onChange: e => setComparison(e), alerts: alerts, alertType: alertType, alertSubtype: alertSubtype,
            onLoadingChange: (isLoading) => handleLoadingChange("comparison", isLoading)
        }, ikeys: {
            grid: { size: 2 },
            label: "", placeholder: t('filters.allIkeys'), value: ikey, onChange: setIkeys, alertType: alertType
        }, nights: {
            grid: { size: 2 }, method: 'POST',
            // i18n: traduce el texto de la opción de noches con plural (1 night / N nights) por el value (count)
            formatOptionLabel: opt => t('units.nights', { count: Number(opt?.value) }),
            value: nights, onChange: setNights, mainHotels: mainHotels, sites: sites || sitesList, occupancy: occupancy,
            devices: devices, date: monthYear, customDates: customDateRange, selectedRoute: connectivities,
            sitePackageID: otas, alertType: alertType, section: section,
            onAjax: (options = []) => {
                const availableOptions = options.filter(opt => !opt.na)
                const valueExists = availableOptions.some(opt => opt.value === nights)
                if (!valueExists && availableOptions.length) {
                    setNights(availableOptions[0])
                    setDefaultNights(availableOptions[0])
                }
                return availableOptions
            },
            onLoadingChange: (isLoading) => handleLoadingChange("nights", isLoading)
        }, nightsStatic: {
            grid: { size: 2 },
            value: nightsStatic, onChange: setNightsStatic
        }, devicesStatic: {
            grid: { size: 2 },
            value: devicesStatic, onChange: setDevicesStatic
        }, priceType: {
            grid: { size: 2 },
            // priceType renders <SelectRate> (fetches RATES = cancellation policies). Translate options by id_rate.
            formatOptionLabel: opt => translateRate(opt?.value, opt?.label),
            value: priceType, onChange: setPriceType, board: boardID, room: rawRooms, date: monthYear, mainHotels: mainHotels,
            nights: nights, occupancy: occupancy, devices: devices, hotels: hotels, sites: sites || sitesList,
            customDates: customDateRange, info: info, section: section,
            onLoadingChange: (isLoading) => handleLoadingChange("priceType", isLoading)
        }, devices: {
            grid: { size: 2 }, method: 'POST',
            // i18n: traduce SOLO el texto mostrado (Desktop/Mobile/App/Basic) por id_device; value (id) intacto.
            formatOptionLabel: opt => translateDevice(opt?.value, opt?.label),
            value: devices, onChange: setDevices, sites: sites || sitesList, occupancy: occupancy, nights: nights,
            mainHotels: mainHotels, date: monthYear, customDates: customDateRange, section: section
        }, mappingType: {
            grid: { size: 2 },
            value: mappingType, onChange: setMappingType
        }, minStay: {
            grid: { size: 2 },
            checked: minStay, onChange: setMinStay, siteName: siteName
        }, adults: {
            grid: { size: 2 },
            value: adults, onChange: setAdults
        }, kids: {
            grid: { size: 2 },
            value: kids, onChange: setKids
        }, cityFrom: {
            grid: { size: 2 },
            value: cityFrom, onChange: setCityFrom,
            onLoadingChange: (isLoading) => handleLoadingChange("cities", isLoading)
        }, cityTo: {
            grid: { size: 2 },
            value: cityTo, onChange: setCityTo,
            onLoadingChange: (isLoading) => handleLoadingChange("cities", isLoading)
        }, info: {
            grid: { size: 2 },
            value: info, onChange: setInfo, mainHotels: mainHotels, rate: priceType, room: rawRooms, date: monthYear, 
            nights: nights, occupancy: occupancy, devices: devices, hotels: hotels, sites: sites || sitesList, 
            customDates: customDateRange, board: boardID,
            onAjax: (options = []) => {
                const valueExists = options.some(opt => opt.value === info)
                if (!valueExists) setInfo({value: null})
                return options
            },
            onLoadingChange: (isLoading) => handleLoadingChange("info", isLoading)
        }, realtimeMarket: {
            grid: { size: 2 },
            value: realtimeMarket, onChange: setRealtimeMarket,
            onLoadingChange: (isLoading) => handleLoadingChange("realtimeMarket", isLoading)
        }
    }, compProps)

    return <CommonFiltersContainer used={false}>
        <GridSeeker container className="filterRow-realTime">
            {myComps.mappingType && <GridSeeker {...myCompProps.mappingType.grid}>
                <SelectMappingType {...myCompProps.mappingType} />
            </GridSeeker>}

            {myComps.sites && <GridSeeker {...myCompProps.sites.grid}>
                <SelectCompetitionSites {...myCompProps.sites} />
            </GridSeeker>}

            {myComps.devicesParity && <GridSeeker {...myCompProps.devicesParity.grid}>
                <RadioDevicesMultiButton {...myCompProps.devicesParity} />
            </GridSeeker>}

            {myComps.alerts && <GridSeeker {...myCompProps.alerts.grid}>
                <RadioAlertMultiButton {...myCompProps.alerts} />
            </GridSeeker>}

            {myComps.alerts && myComps.alertType && <GridSeeker {...myCompProps.alertType.grid}>
                <SelectAlertType {...myCompProps.alertType} />
            </GridSeeker>}
            
            {myComps.alertName && <GridSeeker {...myCompProps.alertName.grid}>
                <SelectAlerts {...myCompProps.alertName} />
            </GridSeeker>}

            {myComps.alerts && alerts === 'parity' && alertType !== '9' && <GridSeeker {...myCompProps.alertSubtype.grid}>
                <SelectAlertSubtype {...myCompProps.alertSubtype} />
            </GridSeeker>}

            {myComps.connectivities && <GridSeeker {...myCompProps.connectivities.grid}>
                <SelectConnectivities {...myCompProps.connectivities} />
            </GridSeeker>}

            {myComps.connectivitiesStatic && <GridSeeker {...myCompProps.connectivities.grid}>
                <SelectConnectivitiesStatic {...myCompProps.connectivities} />
            </GridSeeker>}

            {myComps.otas && <GridSeeker {...myCompProps.otas.grid}>
                <SelectOtas {...myCompProps.otas} />
            </GridSeeker>}

            {myComps.province && <GridSeeker {...myCompProps.province.grid}>
                <SelectProvince {...myCompProps.province} />
            </GridSeeker>}

            {myComps.location && <GridSeeker {...myCompProps.location.grid}>
                <SelectLocation {...myCompProps.location} />
            </GridSeeker>}

            {myComps.hotelHistorical && <GridSeeker {...myCompProps.hotelHistorical.grid}>
                <SelectHotelsHistorical {...myCompProps.hotelHistorical} />
            </GridSeeker>}

            {myComps.year && <GridSeeker {...myCompProps.year.grid}>
                <SelectYear {...myCompProps.year} />
            </GridSeeker>}

            {myComps.hotels && <GridSeeker {...myCompProps.hotels.grid}>
                <SelectHotels {...myCompProps.hotels} />
            </GridSeeker>}

            {myComps.dateRange && myComps.customDateRange && <GridSeeker {...myCompProps.dateRange.grid}>
                <SelectDateRange {...myCompProps.dateRange} />
            </GridSeeker>}

            {useCustomDate && myComps.customDateRange && !calendarType && <GridSeeker {...myCompProps.customDateRange.grid} >
                <CustomDateRangePicker {...myCompProps.customDateRange} />
            </GridSeeker>}

            {myComps.devices && <GridSeeker {...myCompProps.devices.grid}>
                <SelectDevice {...myCompProps.devices} />
            </GridSeeker>}

            {myComps.devicesStatic && <GridSeeker {...myCompProps.devicesStatic.grid}>
                <SelectDeviceStatic {...myCompProps.devicesStatic} />
            </GridSeeker>}

            {myComps.nights && <GridSeeker {...myCompProps.nights.grid}>
                <SelectNights {...myCompProps.nights} />
            </GridSeeker>}

            {myComps.occupancy && <GridSeeker {...myCompProps.occupancy.grid}>
                <SelectPax {...myCompProps.occupancy} />
            </GridSeeker>}

            {myComps.sources && <GridSeeker {...myCompProps.sources.grid}>
                <SelectSources {...myCompProps.sources} />
            </GridSeeker>}

            {myComps.cities && <GridSeeker {...myCompProps.cityFrom.grid}>
                <SelectCityFrom {...myCompProps.cityFrom} />
            </GridSeeker>}

            {myComps.cities && <GridSeeker {...myCompProps.cityTo.grid}>
                <SelectCityTo {...myCompProps.cityTo} />
            </GridSeeker>}

            {myComps.allHotels && <GridSeeker {...myCompProps.allHotels.grid}>
                <SelectAsyncHotels {...myCompProps.allHotels} />
            </GridSeeker>}

            {myComps.siteLocation && <GridSeeker {...myCompProps.siteLocation.grid}>
                <SelectSiteLocation {...myCompProps.siteLocation} />
            </GridSeeker>}

            {myComps.sitesList && <GridSeeker {...myCompProps.sitesList.grid}>
                <SelectSite {...myCompProps.sitesList} />
            </GridSeeker>}

            {myComps.occupancyStatic && <GridSeeker {...myCompProps.occupancy.grid}>
                <SelectPaxStatic {...myCompProps.occupancy} />
            </GridSeeker>}

            {myComps.realtimeMarket && <GridSeeker {...myCompProps.realtimeMarket.grid}>
                <SelectMarket {...myCompProps.realtimeMarket} />
            </GridSeeker>}

            {!!(myComps.comparison && alertType && alertType !== '9') && <GridSeeker {...myCompProps.comparison.grid}>
                <SelectComparison {...myCompProps.comparison} />
            </GridSeeker>}

            {myComps.ikeys && alertType === '9' && <GridSeeker {...myCompProps.ikeys.grid}>
                <SelectIkey {...myCompProps.ikeys} />
            </GridSeeker>}

            {myComps.board && <GridSeeker {...myCompProps.board.grid}>
                <SelectBoard {...myCompProps.board} />
            </GridSeeker>}

            {myComps.adults && <GridSeeker {...myCompProps.adults.grid}>
                <SelectOccupationAdults {...myCompProps.adults} />
            </GridSeeker>}

            {myComps.kids && <GridSeeker {...myCompProps.kids.grid}>
                <SelectOccupationKids {...myCompProps.kids} />
            </GridSeeker>}

            {myComps.calendarType && <GridSeeker {...myCompProps.calendarType.grid}>
                <SelectCalendarType {...myCompProps.calendarType} />
            </GridSeeker>}

            {myComps.customDate && <GridSeeker {...myCompProps.customDate.grid}>
                <CustomDatePicker {...myCompProps.customDate} />
            </GridSeeker>}

            {myComps.customDateRange && calendarType === 'range' && <GridSeeker {...myCompProps.customDateRange.grid}>
                <CustomDateRangePicker {...myCompProps.customDateRange} />
            </GridSeeker>}

            {myComps.specificDays && calendarType === 'specific' && <GridSeeker {...myCompProps.specificDays.grid} >
                <DateSpecificDays {...myCompProps.specificDays} />
            </GridSeeker>}

            {myComps.nightsStatic && <GridSeeker {...myCompProps.nightsStatic.grid}>
                <SelectNightsStatic {...myCompProps.nightsStatic} />
            </GridSeeker>}

            {myComps.rate && <GridSeeker {...myCompProps.rate.grid}>
                <SelectRate {...myCompProps.rate} />
            </GridSeeker>}

            {myComps.priceType && <GridSeeker {...myCompProps.priceType.grid}>
                <SelectRate {...myCompProps.priceType} />
            </GridSeeker>}
            
            {myComps.rooms && <GridSeeker {...myCompProps.rooms.grid}>
                <SelectRooms {...myCompProps.rooms} />
            </GridSeeker>}

            {myComps.rawRooms && <GridSeeker {...myCompProps.rawRooms.grid}>
                <SelectRawRooms {...myCompProps.rawRooms} />
            </GridSeeker>}

            {myComps.minStay && <GridSeeker {...myCompProps.minStay.grid}>
                <SwitchMinStay {...myCompProps.minStay} />
            </GridSeeker>}

            {myComps.info && <GridSeeker {...myCompProps.info.grid}>
                <SelectInfo {...myCompProps.info} />
            </GridSeeker>}

            {myComps.filterBtn && <GridSeeker size={2} className="btn-filter" style={{ marginLeft: 'auto' }}>
                { myComps.buttonsActions && !!nights && !!occupancy && <CommonFiltersActions page={page}/> }
                { myComps.buttonsAlertActions && !!nights && !!occupancy && <CommonFiltersAlertActions /> }
                <div style={{ display: 'flex', gap: 4 }}>
                    { onCancel && <ButtonSeeker
                        variant="contained"
                        onClick={onCancel}
                        className="width-100 capitalize"
                        style={{ background: '#d32f2f', color: 'white', flex: 1 }}
                    >{t('actions.cancel')}</ButtonSeeker> }
                    <SubmitButtonSeeker isLoading={isLoading} onClick={onSubmit} disabled={!isValid} style={{ flex: 1 }} className={`width-100 capitalize button-transition ${transitioning ? 'transitioning' : ''}`}>
                        {isLoading ? t('misc.loading') : (buttonLabel ?? t('actions.filter'))}
                    </SubmitButtonSeeker>
                </div>
            </GridSeeker>}
        </GridSeeker>
    </CommonFiltersContainer>
}

const CommonFiltersContainer = ({ used=true, children, ...props }) => {
    if (!used) return <>{children}</>

    return <AccordionCardSeeker {...props} >
        {children}
    </AccordionCardSeeker>
}